import React from "react";
import {
  autoUpdate,
  flip,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from "@floating-ui/react";

import type { IInputPopoverOptions } from ".";

export function useInputPopover({
  initialOpen = false,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  listNavLoop = true,
  ariaRole,
  disabled,
}: IInputPopoverOptions = {}) {
  const [uncontrolledOpen, _setUncontrolledOpen] = React.useState(initialOpen);
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);

  function setUncontrolledOpen(open: boolean) {
    if (!disabled) {
      _setUncontrolledOpen(open);
    }
  }

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const listRef = React.useRef<(HTMLElement | null)[]>([]);

  const data = useFloating({
    placement: "bottom-start",
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  const context = data.context;

  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: ariaRole,
    enabled: ariaRole !== undefined,
  });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: listNavLoop,
  });

  const interactions = useInteractions([dismiss, role, listNav]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      activeIndex,
      setActiveIndex,
      listRef,
    }),
    [open, setOpen, interactions, data, activeIndex],
  );
}
